import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  create_facial_verification_session,
  fetch_facial_verification_result,
  upload_facial_verification_id_document
} from '@/services/identity_facial_verification_service';
import { create_camera_actions } from './facial_verification_field_camera';
import {
  get_error_message,
  get_error_status,
  score_percent
} from './facial_verification_field_shared';
import {
  apply_created_session,
  apply_restored_result,
  apply_selected_id,
  build_result_request,
  build_session_request,
  build_upload_request,
  get_selected_file,
  validate_id_file
} from './facial_verification_field_state_helpers';

function create_field_state() {
  return {
    file_input: ref(null),
    video: ref(null),
    session: ref(null),
    id_file: ref(null),
    id_preview_url: ref(''),
    step: ref(1),
    loading: ref(false),
    camera_ready: ref(false),
    camera_stream: ref(null),
    current_challenge: ref(null),
    challenge_index: ref(-1),
    countdown: ref(0),
    result: ref(null),
    local_error: ref(''),
    status_message: ref('Preparing verification session...'),
    frames: ref([])
  };
}

function create_field_computed(state) {
  return {
    result_tone: computed(() => String(state.result.value?.decision || '').trim().toLowerCase()),
    score_rows: computed(() => {
      if (!state.result.value) return [];
      return [
        ['Capture quality', state.result.value.quality],
        ['Active liveness', state.result.value.active_liveness],
        ['Capture integrity', state.result.value.capture_integrity],
        ['Selfie to ID', state.result.value.face_match]
      ];
    }),
    current_challenge_class: computed(() => String(state.current_challenge.value?.code || '').trim().toLowerCase()),
    can_upload_id: computed(() => Boolean(state.id_file.value && state.session.value && !state.loading.value)),
    can_start_capture: computed(() => Boolean(state.camera_ready.value && state.session.value && !state.loading.value)),
    is_complete: computed(() => Boolean(state.result.value?.decision))
  };
}

function create_field_emitters(state, derived, emit) {
  function emit_value(extra = {}) {
    emit('update:modelValue', {
      completed: derived.is_complete.value,
      decision: state.result.value?.decision || '',
      status_message: state.status_message.value,
      result: state.result.value,
      id_document_preview_url: state.id_preview_url.value,
      session_id: state.session.value?.session_id || '',
      ...extra
    });
  }

  function revoke_preview_url() {
    if (!state.id_preview_url.value) return;
    URL.revokeObjectURL(state.id_preview_url.value);
    state.id_preview_url.value = '';
  }

  const field_emitters = { emit_value, revoke_preview_url };
  return field_emitters;
}

function create_restore_actions(props, state, emitters) {
  async function restore_existing_result() {
    const request = build_result_request(props);
    if (!request) {
      return;
    }

    try {
      const payload = await fetch_facial_verification_result(request);
      apply_restored_result(state, emitters, payload);
    } catch (error) {
      if (get_error_status(error) !== 404) {
        state.local_error.value = get_error_message(error, 'Unable to load the saved facial verification result.');
      }
    }
  }

  async function ensure_session() {
    const request = build_session_request(props);
    const should_skip = state.session.value || !request || state.result.value;
    if (should_skip) {
      return;
    }

    state.loading.value = true;
    state.local_error.value = '';
    state.status_message.value = 'Preparing verification session...';

    try {
      const payload = await create_facial_verification_session(request);
      apply_created_session(state, emitters, payload);
    } catch (error) {
      state.local_error.value = get_error_message(error, 'Unable to create a facial verification session.');
      state.status_message.value = 'Session could not be created';
    } finally {
      state.loading.value = false;
    }
  }

  const session_actions = {
    restore_existing_result,
    ensure_session
  };

  return session_actions;
}

function create_upload_actions(state, emitters) {
  function choose_id() {
    state.file_input.value?.click();
  }

  function on_id_selected(event) {
    const selection = get_selected_file(event);
    if (!selection) {
      return;
    }

    const error_message = validate_id_file(selection.file);
    if (error_message) {
      state.local_error.value = error_message;
      return;
    }

    apply_selected_id(state, emitters, selection.file, selection.target);
  }

  async function upload_id() {
    const request = build_upload_request(state);
    if (!request) {
      return;
    }

    state.loading.value = true;
    state.local_error.value = '';

    try {
      await upload_facial_verification_id_document(request);
      state.step.value = 2;
      state.status_message.value = 'ID portrait accepted';
      emitters.emit_value();
    } catch (error) {
      state.local_error.value = get_error_message(error, 'Unable to upload the ID document.');
    } finally {
      state.loading.value = false;
    }
  }

  const id_actions = {
    choose_id,
    on_id_selected,
    upload_id
  };

  return id_actions;
}

function create_restart_action(state, emitters, ensure_session, stop_camera) {
  const restart = async function restart() {
    stop_camera();
    state.id_file.value = null;
    state.result.value = null;
    state.frames.value = [];
    state.local_error.value = '';
    state.step.value = 1;
    state.session.value = null;
    emitters.revoke_preview_url();
    emitters.emit_value({ completed: false, decision: '', result: null });
    await ensure_session();
  };

  return restart;
}

function register_field_lifecycle(props, state, emitters, restore_existing_result, ensure_session, stop_camera) {
  watch(
    () => props.applicationFormId,
    async (value) => {
      const question = props.question;
      if (!value || !question?.id) return;
      await restore_existing_result();
      if (!state.result.value) await ensure_session();
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    stop_camera();
    emitters.revoke_preview_url();
  });
}

export function createFacialVerificationFieldState(props, emit) {
  const state = create_field_state();
  const derived = create_field_computed(state);
  const emitters = create_field_emitters(state, derived, emit);
  const { restore_existing_result, ensure_session } = create_restore_actions(props, state, emitters);
  const { choose_id, on_id_selected, upload_id } = create_upload_actions(state, emitters);
  const { start_camera, run_capture, stop_camera } = create_camera_actions(state, emitters);
  const restart = create_restart_action(state, emitters, ensure_session, stop_camera);

  register_field_lifecycle(props, state, emitters, restore_existing_result, ensure_session, stop_camera);

  return {
    ...state,
    ...derived,
    score_percent,
    choose_id,
    on_id_selected,
    upload_id,
    start_camera,
    run_capture,
    restart
  };
}
